/**
 * Character position analyzer - determines optimal search position (2nd/3rd char)
 * based on character frequency analysis
 */

import { PositionAnalysis } from '../../types/search';

/**
 * Character frequency in English words (percentage)
 * Based on analysis of academic/humanities vocabulary
 * Source: Keio entrance exam corpus analysis
 */
const FIRST_CHAR_FREQUENCY: Record<string, number> = {
  a: 8.2,
  b: 4.1,
  c: 7.2,
  d: 6.1,
  e: 6.5,
  f: 3.9,
  g: 3.2,
  h: 4.3,
  i: 5.4,
  j: 0.9,
  k: 1.8,
  l: 3.7,
  m: 4.5,
  n: 3.1,
  o: 4.8,
  p: 7.1,
  q: 0.5,
  r: 5.9,
  s: 8.5,
  t: 7.8,
  u: 2.9,
  v: 1.6,
  w: 3.4,
  x: 0.3,
  y: 1.2,
  z: 0.4,
};

/**
 * Threshold frequencies for determining search strategy
 */
const FREQUENCY_THRESHOLDS = {
  VERY_HIGH: 7.0,  // Use 2nd character search (s, c, t, p, a, e)
  HIGH: 5.0,       // Consider 2nd character search (r, i, o, d, m)
  MEDIUM: 3.0,     // Standard prefix search usually fine
  LOW: 1.5,        // Definitely use prefix search
};

/**
 * Get optimal search position based on query analysis
 * @param query - User's search query
 * @returns Analysis with recommended search position
 */
export function getOptimalSearchPosition(query: string): PositionAnalysis {
  // Handle edge cases
  if (!query || query.length === 0) {
    return {
      optimalPosition: 1,
      reason: 'Empty query - using standard prefix search',
      firstCharFrequency: 0,
      suggestions: ['Start typing to search'],
    };
  }

  if (query.length === 1) {
    const firstChar = query[0]!.toLowerCase();
    const frequency = FIRST_CHAR_FREQUENCY[firstChar] || 0;

    return {
      optimalPosition: 1,
      reason: 'Single character - showing all words starting with this letter',
      firstCharFrequency: frequency,
      suggestions: [
        frequency > FREQUENCY_THRESHOLDS.VERY_HIGH
          ? `'${firstChar}' is very common - type 2nd character for faster search`
          : `Type more characters to narrow results`,
      ],
    };
  }

  const firstChar = query[0]!.toLowerCase();
  const secondChar = query[1]?.toLowerCase() || '';
  const frequency = FIRST_CHAR_FREQUENCY[firstChar] || 0;

  // Handle numbers and special characters
  if (!/[a-z]/.test(firstChar)) {
    return {
      optimalPosition: 1,
      reason: 'Non-alphabetic character - using standard search',
      firstCharFrequency: 0,
      suggestions: ['Enter alphabetic characters'],
    };
  }

  // Determine optimal position based on frequency
  if (frequency >= FREQUENCY_THRESHOLDS.VERY_HIGH) {
    // Very common first character - recommend 2nd char search
    if (query.length >= 2) {
      return {
        optimalPosition: 2,
        reason: `'${firstChar}' is very common (${frequency.toFixed(1)}%) - searching by 2nd character '${secondChar}'`,
        firstCharFrequency: frequency,
        suggestions: [
          `Narrowing to words with '${firstChar}${secondChar}...'`,
          'Type 3rd character for even faster search',
        ],
      };
    }
  } else if (frequency >= FREQUENCY_THRESHOLDS.HIGH) {
    // Moderately common - 2nd char search helps
    if (query.length >= 2) {
      return {
        optimalPosition: 2,
        reason: `'${firstChar}' is fairly common (${frequency.toFixed(1)}%) - using 2nd character '${secondChar}' for efficiency`,
        firstCharFrequency: frequency,
        suggestions: [`Results for '${firstChar}${secondChar}...'`],
      };
    }
  } else if (frequency >= FREQUENCY_THRESHOLDS.MEDIUM) {
    // Medium frequency - prefix search is fine but show 2nd char option
    if (query.length >= 3) {
      const thirdChar = query[2]!.toLowerCase();
      return {
        optimalPosition: 3,
        reason: `Searching by 3rd character '${thirdChar}' for precise results`,
        firstCharFrequency: frequency,
        suggestions: [`Exact matches for '${firstChar}${secondChar}${thirdChar}...'`],
      };
    } else if (query.length >= 2) {
      return {
        optimalPosition: 2,
        reason: `Using 2nd character '${secondChar}' for better precision`,
        firstCharFrequency: frequency,
        suggestions: [`Type 3rd character for exact matches`],
      };
    }
  } else {
    // Low frequency - prefix search is efficient
    return {
      optimalPosition: 1,
      reason: `'${firstChar}' is uncommon (${frequency.toFixed(1)}%) - standard prefix search is efficient`,
      firstCharFrequency: frequency,
      suggestions: [`Few words start with '${firstChar}' - standard search works well`],
    };
  }

  // Default to prefix search
  return {
    optimalPosition: 1,
    reason: 'Using standard prefix search',
    firstCharFrequency: frequency,
    suggestions: ['Continue typing to narrow results'],
  };
}

/**
 * Get character frequency for a specific character
 * @param char - Character to check (case-insensitive)
 * @returns Frequency percentage (0-100)
 */
export function getCharacterFrequency(char: string): number {
  if (!char || char.length === 0) return 0;
  return FIRST_CHAR_FREQUENCY[char.toLowerCase()] || 0;
}

/**
 * Check if a character is high-frequency (should use position-based search)
 * @param char - Character to check
 * @returns true if character is high-frequency
 */
export function isHighFrequencyChar(char: string): boolean {
  const frequency = getCharacterFrequency(char);
  return frequency >= FREQUENCY_THRESHOLDS.HIGH;
}

/**
 * Get all character frequencies sorted by frequency
 * @returns Array of [character, frequency] tuples sorted by frequency (descending)
 */
export function getAllCharacterFrequencies(): Array<[string, number]> {
  return Object.entries(FIRST_CHAR_FREQUENCY).sort((a, b) => b[1] - a[1]);
}

/**
 * Get search strategy recommendation
 * @param query - User's search query
 * @returns Recommended search strategy description
 */
export function getSearchStrategy(query: string): string {
  const analysis = getOptimalSearchPosition(query);

  if (analysis.optimalPosition === 1) {
    return 'prefix';
  } else if (analysis.optimalPosition === 2) {
    return 'position-2';
  } else if (analysis.optimalPosition === 3) {
    return 'position-3';
  }

  return 'prefix';
}

/**
 * Suggest optimal query format based on first character
 * @param firstChar - First character of search
 * @returns Suggestion for how to search efficiently
 */
export function suggestQueryFormat(firstChar: string): string {
  const frequency = getCharacterFrequency(firstChar);

  if (frequency >= FREQUENCY_THRESHOLDS.VERY_HIGH) {
    return `Try typing 2-3 characters for '${firstChar}' words (very common letter)`;
  } else if (frequency >= FREQUENCY_THRESHOLDS.HIGH) {
    return `Type 2nd character for faster results with '${firstChar}'`;
  } else if (frequency < FREQUENCY_THRESHOLDS.LOW) {
    return `'${firstChar}' is rare - even 1 character narrows results well`;
  }

  return `Type 2-3 characters for best results`;
}

/**
 * Get frequency category for a character
 */
export function getFrequencyCategory(
  char: string
): 'very-high' | 'high' | 'medium' | 'low' | 'very-low' {
  const frequency = getCharacterFrequency(char);

  if (frequency >= FREQUENCY_THRESHOLDS.VERY_HIGH) return 'very-high';
  if (frequency >= FREQUENCY_THRESHOLDS.HIGH) return 'high';
  if (frequency >= FREQUENCY_THRESHOLDS.MEDIUM) return 'medium';
  if (frequency >= FREQUENCY_THRESHOLDS.LOW) return 'low';
  return 'very-low';
}
